import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { govPageContainer, GOV_TYPE } from '../ui/PatientUIGovernor';
import SubstitutionRequests from '../components/SubstitutionRequests';
import type { PharmacyOrderFull } from '../api';
import { useStore, store } from '../state/patientProductionStore';
import type { PharmacyOrder } from '../state/patientProductionStore';
import { patientApi, type PrescriptionRecord } from '../api';
import { useToast } from '../../../components/ui/Toast';
import { pharmacyPublicApi, listRememberedPharmacyOrders } from '../pharmacyPublicApi';
import type { PublicTrackedOrder } from '../pharmacyPublicApi';

type FilterTab = 'ALL' | 'PLACED' | 'DISPATCHED' | 'DELIVERED' | 'CANCELLED';

const STATUS_COLOR: Record<string, string> = {
  PLACED: T.info,
  CONFIRMED: T.info,
  DISPATCHED: T.warning,
  DELIVERED: T.success,
  CANCELLED: T.danger,
};

const FILTER_TABS: FilterTab[] = ['ALL', 'PLACED', 'DISPATCHED', 'DELIVERED', 'CANCELLED'];

/* ── SVG Icons (inline) ─────────────────────────────────────────────── */

const IconShoppingBag = ({ size = 18, color = T.textSec }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 01-8 0" />
  </svg>
);

const IconPill = ({ size = 14, color = T.textSec }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.5 1.5l-8 8a5.66 5.66 0 008 8l8-8a5.66 5.66 0 00-8-8z" />
    <line x1="6.5" y1="13.5" x2="13.5" y2="6.5" />
  </svg>
);

const IconX = ({ size = 14, color = T.danger }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IconClipboard = ({ size = 16, color = T.teal }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
  </svg>
);

const IconPlus = ({ size = 12, color = T.white }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IconCalendar = ({ size = 12, color = T.textTer }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const IconRupee = ({ size = 11, color = T.text }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
    <line x1="6" y1="3" x2="18" y2="3" />
    <line x1="6" y1="9" x2="18" y2="9" />
    <path d="M9 3c0 6 6 6 6 12H9" />
  </svg>
);

// Backend statuses PACKED/OUT_FOR_DELIVERY both map to this page's single
// DISPATCHED bucket — introducing new statuses into the hardcoded 5-tab
// filter/color model here is a bigger UI change than this fix calls for.

/** Pharmacy ONLINE_STORE order statuses → this page's buckets. */
function publicStatusToBucket(status: string): PharmacyOrder['status'] {
  const v = status.toUpperCase();
  if (v === 'CANCELLED' || v === 'REJECTED') return 'CANCELLED';
  if (v === 'COMPLETED' || v === 'DELIVERED' || v === 'HANDED_OVER') return 'DELIVERED';
  if (v === 'PACKED' || v === 'OUT_FOR_DELIVERY' || v === 'READY_FOR_PICKUP' || v === 'DISPATCHED') return 'DISPATCHED';
  return 'PLACED'; // RECEIVED / ACCEPTED / READY_FOR_PAYMENT — still with the pharmacy
}

/** A live-tracked online-store order → this page's card shape. */
function trackedToOrder(t: PublicTrackedOrder, fallbackPharmacy: string): PharmacyOrder {
  return {
    id: t.orderNumber,
    medicines: t.items.map((i) => ({ name: i.medicineName, qty: i.quantity, dosage: i.strength || '', unitPrice: undefined })),
    pharmacyName: t.pharmacy?.name || fallbackPharmacy,
    status: publicStatusToBucket(t.status),
    totalAmount: t.totalAmount,
    deliveryMode: t.fulfillmentType === 'DELIVERY' ? 'HOME' : 'PICKUP',
    deliveryAddress: undefined,
    createdAt: t.placedAt ?? new Date().toISOString(),
  } as PharmacyOrder;
}

/**
 * The patient API's pharmacy orders are a DIFFERENT set of records from the
 * marketplace medicine orders this page already listed — different ids, a
 * different numbering scheme (PH-ORD-… against NHC-ORD-…), and they are the
 * ones invoices and payments are raised against. Showing only one set meant a
 * patient's billed pharmacy orders were missing from their own orders page.
 */
function fullToStoreOrder(d: PharmacyOrderFull): PharmacyOrder & { orderNumber?: string; patientOrderId?: string | null } {
  const statusMap: Record<string, string> = {
    PACKED: 'DISPATCHED',
    OUT_FOR_DELIVERY: 'DISPATCHED',
    READY_FOR_PICKUP: 'CONFIRMED',
  };
  return {
    id: d.id,
    // Carried so this row can be cancelled through the marketplace order it
    // came from, and so the marketplace copy of it is not listed twice.
    patientOrderId: d.patientOrderId ?? null,
    medicines: (d.items ?? []).map((i) => ({
      name: i.medicineName ?? i.name ?? 'Medicine',
      qty: i.quantity ?? 1,
      dosage: '',
      unitPrice: i.unitPrice,
    })),
    pharmacyName: d.pharmacy?.name ?? 'Pharmacy',
    status: (statusMap[(d.status || '').toUpperCase()] ?? d.status) as PharmacyOrder['status'],
    totalAmount: d.totalAmount ?? 0,
    deliveryFee: d.deliveryFee,
    paymentStatus: (d.paymentStatus as PharmacyOrder['paymentStatus']) ?? undefined,
    paymentMethod: (d.paymentMethod as PharmacyOrder['paymentMethod']) ?? undefined,
    deliveryMode: d.fulfillmentType === 'DELIVERY' ? 'HOME' : 'PICKUP',
    deliveryAddress: d.delivery?.address ?? undefined,
    prescriptionId: d.prescriptionId ?? undefined,
    createdAt: d.placedAt,
    orderNumber: d.orderNumber,
  };
}

function toStoreOrder(d: any): PharmacyOrder {
  return {
    id: d.id,
    medicines: (d.items ?? []).map((i: any) => ({ name: i.name, qty: i.quantity, dosage: i.dosage || '', unitPrice: i.unitPrice ?? undefined })),
    pharmacyName: d.providerName || 'Pharmacy',
    status: d.status === 'PACKED' || d.status === 'OUT_FOR_DELIVERY' ? 'DISPATCHED' : d.status,
    totalAmount: d.totalAmount ?? 0,
    deliveryMode: d.deliveryMode === 'DELIVERY' ? 'HOME' : d.deliveryMode,
    deliveryAddress: d.deliveryAddress || undefined,
    // The backend links an order to the prescription it fulfils; without
    // carrying it here the "Prescription: …" badge below never appears.
    prescriptionId: d.prescriptionId ?? undefined,
    createdAt: d.createdAt,
    // Carried so the richer patient-API view of the same order can be matched.
    orderNumber: d.orderCode ?? d.orderNumber ?? undefined,
    // False means the pharmacy was never told about this order. Saying so is
    // the difference between "waiting on the chemist" and "waiting on nobody".
    sentToPharmacy: d.sentToPharmacy !== false,
  } as PharmacyOrder & { orderNumber?: string; sentToPharmacy?: boolean };
}

export default function PharmacyOrders() {
  const navigate = useNavigate();
  const toast = useToast();
  const storeOrders = useStore(s => s.pharmacyOrders);
  // Real signed prescriptions from the backend — the "Order from
  // Prescription" section was permanently empty reading the local store.
  const [apiPrescriptions, setApiPrescriptions] = useState<PrescriptionRecord[]>([]);
  useEffect(() => {
    let alive = true;
    patientApi.getPrescriptions()
      .then((rows) => { if (alive) setApiPrescriptions(rows); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const prescriptions = apiPrescriptions.map((rx) => ({
    id: rx.id,
    doctorName: rx.doctorName,
    hospital: rx.clinicName || '',
    date: rx.signedAt || rx.createdAt,
    diagnosis: rx.diagnosis,
    medicines: rx.medicines.map((m: any) => ({
      name: String(m.medicineName ?? m.name ?? m.genericName ?? 'Medicine'),
      dosage: [m.strength, m.dosage].filter(Boolean).join(' '),
      frequency: String(m.frequency ?? ''),
      duration: m.duration ? String(m.duration) : '',
    })),
  }));
  const [apiOrders, setApiOrders] = useState<PharmacyOrder[]>([]);
  // Orders placed on a pharmacy's online store — hydrated LIVE from the
  // pharmacy backend via orderNumber + phone (the tracking credentials saved
  // at checkout). This is the real status straight from the pharmacy's queue.
  const [trackedOrders, setTrackedOrders] = useState<PharmacyOrder[]>([]);
  const [trackedPharmacyPhones, setTrackedPharmacyPhones] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<FilterTab>('ALL');
  /**
   * The patient-API view of the same orders, keyed by order number. It
   * carries what the plain order list does not — the pharmacy's timeline,
   * the invoice and the payment state — so an open card can show where an
   * order actually is rather than just its status word.
   */
  const [pharmacyOrders, setPharmacyOrders] = useState<PharmacyOrderFull[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);

  const loadApi = useCallback(() => {
    patientApi.getPharmacyOrders()
      .then(data => setApiOrders((data ?? []).map(toStoreOrder)))
      .catch(() => {});
    // A separate set of records from the marketplace orders above; a
    // failure here costs those rows, not the whole page.
    patientApi.pharmacyModule.orders()
      .then((rows) => setPharmacyOrders(rows ?? []))
      .catch(() => setPharmacyOrders([]));
    const remembered = listRememberedPharmacyOrders().slice(0, 12);
    Promise.all(remembered.map(async (r) => {
      try {
        const t = await pharmacyPublicApi.trackOrder(r.orderNumber, r.phone);
        return { order: trackedToOrder(t, r.pharmacyName), phone: t.pharmacy?.phone ?? null };
      } catch {
        return null; // order not disclosable / network — the local mirror still shows it
      }
    })).then((rows) => {
      const ok = rows.filter((x): x is NonNullable<typeof x> => x !== null);
      setTrackedOrders(ok.map((x) => x.order));
      setTrackedPharmacyPhones(Object.fromEntries(ok.filter((x) => x.phone).map((x) => [x.order.id, x.phone as string])));
    });
  }, []);

  useEffect(() => { loadApi(); }, [loadApi]);

  const trackedIds = new Set(trackedOrders.map(o => o.id));
  const apiIds = new Set(apiOrders.map(o => o.id));
  const localById = new Map(storeOrders.map(o => [o.id, o]));
  // Precedence: live-tracked pharmacy orders (freshest status, merged with the
  // richer local mirror) → legacy marketplace orders → local-only mirrors.
  // The patient API's pharmacy orders, in this page's card shape. They are
  // matched on order number so a record that somehow appears in both sources
  // is shown once.
  const pharmacyRows = pharmacyOrders.map(fullToStoreOrder);
  const pharmacyNumbers = new Set(pharmacyRows.map(r => r.orderNumber));
  const fullById = Object.fromEntries(pharmacyOrders.map(o => [o.id, o]));
  /**
   * A marketplace order now also lands in the pharmacy's own orderbook, so the
   * same order arrives from both lists. The pharmacy's row is the one to keep —
   * it carries the desk's real status, timeline and invoice — and the
   * marketplace copy of it is dropped rather than shown a second time.
   */
  const bridgedIds = new Set(
    pharmacyRows.map(r => r.patientOrderId).filter((v): v is string => !!v),
  );

  const orders = [
    ...pharmacyRows,
    ...trackedOrders.map(o => { const local = localById.get(o.id); return local ? { ...local, status: o.status, totalAmount: o.totalAmount } : o; }),
    ...apiOrders.filter(o => !trackedIds.has(o.id) && !bridgedIds.has(o.id)).map(o => { const local = localById.get(o.id); return local ? { ...local, status: o.status } : o; }),
    ...storeOrders.filter(o => !apiIds.has(o.id) && !trackedIds.has(o.id) && !bridgedIds.has(o.id) && !pharmacyNumbers.has((o as { orderNumber?: string }).orderNumber)),
  ];

  const filtered = activeTab === 'ALL' ? orders : orders.filter(o => o.status === activeTab);

  const handleCancel = async (id: string) => {
    // A row that came from the pharmacy's orderbook but started as a
    // marketplace order is still the patient's to cancel — through the order
    // they placed, which the server then withdraws from the pharmacy too.
    const bridged = pharmacyRows.find(r => r.id === id)?.patientOrderId;
    if (bridged) {
      if (!confirm('Cancel this order?')) return;
      try {
        await patientApi.cancelPharmacyOrder(bridged);
        toast.success('Order cancelled');
        loadApi();
      } catch (e: any) {
        toast.error(e.message || 'Could not cancel order.');
      }
      return;
    }
    if (trackedIds.has(id)) {
      // Online-store orders are cancelled by the pharmacy desk — there is no
      // self-serve cancel endpoint, so never fake one.
      const ph = trackedPharmacyPhones[id];
      toast.info(ph ? `To cancel, call the pharmacy at ${ph} with order ${id}.` : `To cancel, contact the pharmacy with order ${id}.`);
      return;
    }
    if (!confirm('Cancel this order?')) return;
    if (apiIds.has(id)) {
      try {
        await patientApi.cancelPharmacyOrder(id);
        toast.success('Order cancelled');
        loadApi();
      } catch (e: any) {
        toast.error(e.message || 'Could not cancel order.');
      }
    } else {
      store.updatePharmacyOrderStatus(id, 'CANCELLED');
    }
  };

  const handleOrderFromPrescription = (rx: typeof prescriptions[number]) => {
    window.location.href = `/patient/care-services/pharmacy?prescriptionId=${rx.id}`;
  };

  const fmtDate = (d: string) => {
    try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  /* ── Styles ──────────────────────────────────────────────────────── */

  const cardStyle: React.CSSProperties = {
    background: T.white,
    border: `1px solid ${T.border}`,
    borderRadius: 16,
    padding: 14,
    boxShadow: T.shadow,
    marginBottom: 10,
  };

  const badgeStyle = (status: string): React.CSSProperties => ({
    display: 'inline-block',
    fontSize: 9,
    fontWeight: 700,
    fontFamily: T.font,
    padding: '2px 8px',
    borderRadius: 6,
    color: T.white,
    background: STATUS_COLOR[status] || T.textTer,
    letterSpacing: 0.3,
    textTransform: 'uppercase' as const,
  });

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '6px 14px',
    borderRadius: 20,
    border: 'none',
    fontSize: 11,
    fontWeight: 600,
    fontFamily: T.font,
    cursor: 'pointer',
    background: active ? T.teal : T.ice,
    color: active ? T.white : T.textSec,
    transition: 'background 0.15s, color 0.15s',
    whiteSpace: 'nowrap',
  });

  return (
    <div style={{ ...govPageContainer({ padding: '16px 16px 100px' }) }}>

      {/* ── Header ───────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: T.teal,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconShoppingBag size={18} color={T.white} />
        </div>
        <div>
          <div style={{ ...GOV_TYPE.pageTitle }}>Pharmacy Orders</div>
          <div style={{ fontSize: 11, color: T.textTer, marginTop: 2 }}>Order and track your medicines</div>
        </div>
      </div>

      {/* A pharmacy proposed a different medicine and cannot proceed without an answer */}
      <SubstitutionRequests onDecided={loadApi} />

      {/* ── Filter Tabs ──────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 14, paddingBottom: 2 }}>
        {FILTER_TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={tabStyle(activeTab === tab)}
          >
            {tab === 'ALL' ? 'All' : tab.charAt(0) + tab.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {/* ── Active Orders ────────────────────────────────────────── */}
      <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 8 }}>
        Active Orders ({filtered.length})
      </div>

      {filtered.length === 0 ? (
        <div
          style={{
            ...cardStyle,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 32,
          }}
        >
          <IconShoppingBag size={36} color={T.border} />
          <div style={{ fontSize: 11, color: T.textSec, marginTop: 10, fontWeight: 600 }}>
            No orders found
          </div>
          <div style={{ fontSize: 9, color: T.textTer, marginTop: 3, textAlign: 'center' }}>
            {activeTab === 'ALL'
              ? 'Order medicines from your prescriptions below'
              : `No ${activeTab.toLowerCase()} orders`}
          </div>
        </div>
      ) : (
        filtered.map(order => {
          const full = fullById[order.id];
          return (
          <div key={order.id} style={cardStyle}>
            {/* Top row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{order.pharmacyName}</span>
                  <span style={badgeStyle(order.status)}>{order.status}</span>
                </div>
                {(order as { orderNumber?: string }).orderNumber && (
                  <div style={{ fontSize: 9, color: T.textTer, marginTop: 2 }}>
                    {(order as { orderNumber?: string }).orderNumber}
                  </div>
                )}
                {order.prescriptionId && (
                  <div style={{ fontSize: 9, color: T.textTer, marginTop: 2 }}>
                    Prescription: {order.prescriptionId.slice(0, 8)}...
                  </div>
                )}
                {(order as { sentToPharmacy?: boolean }).sentToPharmacy === false && (
                  <div style={{ fontSize: 9, color: T.warning, marginTop: 2, fontWeight: 600 }}>
                    Not yet with the pharmacy — call them to confirm
                  </div>
                )}
              </div>
              {(order.status === 'PLACED' || order.status === 'CONFIRMED') && (
                <button
                  onClick={() => handleCancel(order.id)}
                  style={{
                    background: 'none',
                    border: `1px solid ${T.danger}`,
                    borderRadius: 8,
                    padding: '4px 10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 9,
                    fontWeight: 600,
                    fontFamily: T.font,
                    color: T.danger,
                  }}
                >
                  <IconX size={10} color={T.danger} />
                  Cancel
                </button>
              )}
            </div>

            {/* Medicine list */}
            <div
              style={{
                background: T.ice,
                borderRadius: 10,
                padding: 10,
                marginBottom: 8,
              }}
            >
              <div style={{ fontSize: 9, fontWeight: 700, color: T.textTer, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Medicines
              </div>
              {order.medicines.map((m, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '3px 0',
                    borderBottom: i < order.medicines.length - 1 ? `1px solid ${T.borderLight}` : 'none',
                  }}
                >
                  <IconPill size={10} color={T.teal} />
                  <span style={{ fontSize: 11, color: T.text, fontWeight: 500, flex: 1 }}>{m.name}</span>
                  <span style={{ fontSize: 9, color: T.textTer }}>{m.dosage}</span>
                  <span style={{ fontSize: 9, color: T.textTer }}>x{m.qty}</span>
                </div>
              ))}
            </div>

            {/* Payment + delivery info */}
            {(order.paymentMethod || order.deliveryMode) && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {order.paymentMethod && (
                  <span style={{ fontSize: 9, fontWeight: 600, fontFamily: T.font, color: T.navy, background: `${T.navy}10`, borderRadius: 6, padding: '2px 8px' }}>
                    {order.paymentMethod.replace('_', ' ')}
                  </span>
                )}
                {order.deliveryMode && (
                  <span style={{ fontSize: 9, fontWeight: 600, fontFamily: T.font, color: T.purple, background: `${T.purple}10`, borderRadius: 6, padding: '2px 8px' }}>
                    {order.deliveryMode === 'HOME' ? 'Home Delivery' : 'Store Pickup'}
                  </span>
                )}
                {order.paymentStatus === 'COMPLETED' && (
                  <span style={{ fontSize: 9, fontWeight: 600, fontFamily: T.font, color: T.success, background: `${T.success}10`, borderRadius: 6, padding: '2px 8px' }}>
                    Paid
                  </span>
                )}
              </div>
            )}

            {/* Footer */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <IconCalendar size={10} color={T.textTer} />
                <span style={{ fontSize: 9, color: T.textTer }}>{fmtDate(order.createdAt)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <IconRupee size={11} color={T.text} />
                <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
                  {order.totalAmount.toLocaleString('en-IN')}
                </span>
              </div>
            </div>

            {/* Receipt link */}
            {order.receiptId && (
              <button
                onClick={() => navigate(`/patient/pharmacy/receipt/${order.receiptId}`)}
                style={{
                  marginTop: 8, width: '100%', fontFamily: T.font, fontSize: 10, fontWeight: 600,
                  color: T.teal, background: `${T.teal}08`, border: `1px solid ${T.teal}20`,
                  borderRadius: 8, padding: '7px 0', cursor: 'pointer', textAlign: 'center',
                }}
              >
                View Receipt
              </button>
            )}

            {/* The pharmacy's own record of this order: where it is, and the
                invoice, from the patient API rather than the plain list. */}
            {full && (
              <>
                <button
                  onClick={() => setOpenId(openId === order.id ? null : order.id)}
                  style={{
                    marginTop: 8, width: '100%', fontFamily: T.font, fontSize: 10, fontWeight: 600,
                    color: T.textSec, background: T.white, border: `1px solid ${T.border}`,
                    borderRadius: 8, padding: '7px 0', cursor: 'pointer', textAlign: 'center',
                  }}
                >
                  {openId === order.id ? 'Hide progress' : 'Order progress'}
                </button>

                {openId === order.id && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ fontSize: 9.5, color: T.textSec, fontFamily: T.font }}>
                      {[
                        full.orderNumber,
                        full.invoice?.invoiceNumber ? `Invoice ${full.invoice.invoiceNumber}` : null,
                        full.paymentStatus ? `Payment ${full.paymentStatus.toLowerCase()}` : null,
                        full.fulfillmentType,
                      ].filter(Boolean).join(' · ')}
                    </div>

                    {full.delivery?.address && (
                      <div style={{ fontSize: 9.5, color: T.textSec, fontFamily: T.font }}>
                        Delivering to {full.delivery.address}
                        {full.delivery.etaMinutes != null ? ` · about ${full.delivery.etaMinutes} min` : ''}
                      </div>
                    )}

                    {full.timeline?.length > 0 && full.timeline.map((ev) => (
                      <div key={`${ev.status}-${ev.at}`} style={{ fontSize: 9.5, color: T.textSec, fontFamily: T.font }}>
                        {ev.note || ev.status.replace(/_/g, ' ').toLowerCase()}
                        <span style={{ color: T.textTer }}>
                          {' — '}
                          {new Date(ev.at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true })}
                        </span>
                      </div>
                    ))}

                    {full.cancellationReason && (
                      <div style={{ fontSize: 9.5, color: T.danger, fontFamily: T.font }}>
                        Cancelled: {full.cancellationReason}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          );
        })
      )}

      <div style={{ textAlign: 'center', padding: '16px 0 4px' }}>
        <span style={{ fontFamily: T.font, fontSize: 9, color: T.textTer, lineHeight: 1.6 }}>
          NeoHealthCard™ · Online-store orders are tracked live from the pharmacy · Statuses update when the pharmacy acts
        </span>
      </div>

      {/* ── Order from Prescription ──────────────────────────────── */}
      <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginTop: 20, marginBottom: 8 }}>
        Order from Prescription
      </div>

      {prescriptions.length === 0 ? (
        <div
          style={{
            ...cardStyle,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: 24,
          }}
        >
          <IconClipboard size={28} color={T.border} />
          <div style={{ fontSize: 11, color: T.textSec, marginTop: 8, fontWeight: 600 }}>
            No prescriptions available
          </div>
          <div style={{ fontSize: 9, color: T.textTer, marginTop: 3, textAlign: 'center' }}>
            Prescriptions from your doctors will appear here
          </div>
        </div>
      ) : (
        prescriptions.map(rx => (
          <div key={rx.id} style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{rx.doctorName}</div>
                <div style={{ fontSize: 9, color: T.textTer, marginTop: 1 }}>
                  {rx.hospital} &middot; {fmtDate(rx.date)}
                </div>
                {rx.diagnosis && (
                  <div style={{ fontSize: 9, color: T.info, marginTop: 2, fontWeight: 500 }}>
                    {rx.diagnosis}
                  </div>
                )}
              </div>
              <IconClipboard size={16} color={T.teal} />
            </div>

            {/* Prescription medicines */}
            <div
              style={{
                background: T.ice,
                borderRadius: 10,
                padding: 10,
                marginBottom: 10,
              }}
            >
              {rx.medicines.map((m, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '3px 0',
                    borderBottom: i < rx.medicines.length - 1 ? `1px solid ${T.borderLight}` : 'none',
                  }}
                >
                  <IconPill size={10} color={T.teal} />
                  <span style={{ fontSize: 11, color: T.text, fontWeight: 500, flex: 1 }}>{m.name}</span>
                  <span style={{ fontSize: 9, color: T.textTer }}>{m.dosage} &middot; {m.frequency} &middot; {m.duration}</span>
                </div>
              ))}
            </div>

            {/* Medicine count + order button — no invented price estimate;
                the pharmacy quotes the real total on the order */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 9, color: T.textTer }}>
                {rx.medicines.length} medicine{rx.medicines.length !== 1 ? 's' : ''} · priced by the pharmacy at order
              </div>
              <button
                onClick={() => handleOrderFromPrescription(rx)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '7px 14px',
                  borderRadius: 10,
                  border: 'none',
                  background: T.teal,
                  color: T.white,
                  fontSize: 11,
                  fontWeight: 700,
                  fontFamily: T.font,
                  cursor: 'pointer',
                }}
              >
                <IconPlus size={11} color={T.white} />
                Order Medicines
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
